import { confirmar } from '@/components/ui/confirm-dialog';
import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  RefreshCw,
  Loader2,
  Upload,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/lib/hooks/use-toast';
import { useIntegrationStore } from '@/features/integrations/store/integration-store';
import { useMasterDataStore } from '../store/master-data-store';
import { useMaterialStore } from '@/features/materials/store/material-store';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { Currency, LOB, Country, QuoteType, ImportResult } from '../types/master-data.types';
import { Material } from '@/features/materials/types/material.types';
import { CurrencyTable } from '../components/currency-table';
import { CurrencyForm } from '../components/currency-form';
import { LOBTable } from '../components/lob-table';
import { LOBForm } from '../components/lob-form';
import { CountryTable } from '../components/country-table';
import { ProfitCenterTable } from '../components/profit-center-table';
import { CountryForm } from '../components/country-form';
import { MaterialTable } from '@/features/materials/components/material-table';
import { MaterialForm } from '@/features/materials/components/material-form';
import { TariffHistoryDialog } from '@/features/materials/components/tariff-history-dialog';
import { QuoteTypeTable } from '../components/quote-type-table';
import { QuoteTypeForm } from '../components/quote-type-form';
import { MasterDataImportDialog } from '../components/import-csv-dialog';
import { FreightProviderTable } from '../components/freight-provider-table';
import { IncotermConfigTable } from '../components/incoterm-config-table';
import { ExchangeRateManager } from '../components/exchange-rate-manager';
import { FreightLegCatalogTable } from '../components/freight-leg-catalog-table';
import { FreightServiceTable } from '../components/freight-service-table';
import { FreightLocationTable } from '../components/freight-location-table';
import { masterDataService } from '../services/master-data-service';

// El catálogo arancelario ya no es una pestaña de acá: vive en su propia
// sección, el Asistente Arancelario (/tariff-assistant). La URL vieja
// redirige desde el router.
type TabType =
  | 'currencies'
  | 'exchange-rates'
  | 'lobs'
  | 'countries'
  | 'materials'
  | 'quote-types'
  | 'freight-providers'
  | 'incoterms'
  | 'freight-leg-catalog'
  | 'freight-services'
  | 'freight-locations'
  | 'profit-centers';

const IMPORT_CONFIGS: Partial<
  Record<
    TabType,
    {
      entityName: string;
      headers: string[];
      rows: string[][];
      importFn: (file: File) => Promise<ImportResult>;
    }
  >
> = {
  currencies: {
    entityName: 'Divisas',
    headers: ['code', 'name', 'symbol', 'exchangeRate', 'isBase', 'sapCode'],
    rows: [
      ['USD', 'Dólar Americano', '$', '1', 'true', ''],
      ['EUR', 'Euro', '€', '0.92', 'false', ''],
      ['BOB', 'Boliviano', 'Bs', '0.144', 'false', ''],
    ],
    importFn: (file) => masterDataService.importCurrencies(file),
  },
  lobs: {
    entityName: 'Líneas de Negocio',
    headers: [
      'name',
      'code',
      'level',
      'parentCode',
      'description',
      'targetMarginPercentage',
      'floorMarginPercentage',
      'pctGarantia',
      'pctNacionalizacion',
      'sapCode',
    ],
    rows: [
      ['Tecnología', 'TECH', '1', '', 'Sector tecnológico', '25', '15', '', '', ''],
      ['Software', 'SW', '2', 'TECH', 'Software y aplicaciones', '30', '20', '', '', ''],
      ['Lenovo', 'LENOVO', '3', 'SW', 'Marca Lenovo', '', '', '2.0', '1.9', ''],
    ],
    importFn: (file) => masterDataService.importLobs(file),
  },
  countries: {
    entityName: 'Países',
    headers: ['name', 'code', 'sapCode'],
    rows: [
      ['Bolivia', 'BO', ''],
      ['Colombia', 'CO', ''],
      ['Perú', 'PE', ''],
      ['Argentina', 'AR', ''],
    ],
    importFn: (file) => masterDataService.importCountries(file),
  },
  materials: {
    entityName: 'Materiales',
    headers: [
      'code',
      'name',
      'description',
      'type',
      'externalCode',
      'tariffHeading',
      'tariffRate',
      'lobCode',
      'sku',
      'brand',
      'model',
      'unitOfMeasure',
      'weight',
      'width',
      'height',
      'depth',
      'sapCode',
    ],
    rows: [
      [
        'MAT-001',
        'Laptop HP Pavilion 15',
        'Laptop 15 pulgadas i5 8GB RAM 512GB SSD',
        'PRODUCT',
        'HP-PAV-15',
        '8471.30.00',
        '15',
        'TECH',
        'SKU-LP-001',
        'HP',
        'Pavilion 15-eh1010la',
        'UND',
        '1.75',
        '36',
        '24',
        '2',
        '',
      ],
      [
        'MAT-002',
        'Mouse Logitech M100',
        'Mouse USB óptico',
        'PRODUCT',
        'LOG-M100',
        '8471.60.90',
        '12',
        'TECH',
        'SKU-MS-001',
        'Logitech',
        'M100',
        'UND',
        '0.085',
        '6',
        '3.5',
        '11',
        '',
      ],
      [
        'SRV-001',
        'Instalación on-site',
        'Servicio de instalación en sitio del cliente',
        'SERVICE',
        '',
        '',
        '',
        'TECH',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importMaterials(file),
  },
  'exchange-rates': {
    entityName: 'Tipos de Cambio',
    headers: [
      'fromCurrencyCode',
      'toCurrencyCode',
      'rate',
      'effectiveDate',
      'source',
      'notes',
      'sapCode',
    ],
    rows: [
      ['USD', 'BOB', '6.96', '2026-01-01', 'manual', 'TC oficial', ''],
      ['EUR', 'BOB', '7.55', '2026-01-01', 'manual', '', ''],
    ],
    importFn: (file) => masterDataService.importExchangeRates(file),
  },
  'quote-types': {
    entityName: 'Tipos de Cotización',
    headers: [
      'code',
      'name',
      'description',
      'codePrefix',
      'order',
      'upstreamTypeCode',
      'upstreamVariableCode',
      'upstreamTotalVariableCode',
      'sapCode',
    ],
    rows: [
      [
        'IMPORT',
        'Cotización de Importación',
        'Quote upstream para venta',
        'IMP',
        '1',
        '',
        '',
        '',
        '',
      ],
      [
        'SALE',
        'Venta Final',
        'Cotización al cliente',
        'VTA',
        '2',
        'IMPORT',
        'CIF_UNIT',
        'TOTAL_CIF',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importQuoteTypes(file),
  },
  'profit-centers': {
    entityName: 'Centros de Beneficio',
    headers: [
      'code',
      'name',
      'longName',
      'controllingArea',
      'segment',
      'hierarchy',
      'responsibleUser',
    ],
    rows: [
      ['PC001', 'Tecnología', 'División Tecnología', 'A000', 'SEG_TECH', 'HIER_MAIN', 'jperez'],
      ['PC002', 'Servicios', 'División Servicios', 'A000', 'SEG_SVC', 'HIER_MAIN', ''],
    ],
    importFn: (file) => masterDataService.importProfitCenters(file),
  },
  incoterms: {
    entityName: 'Incoterms',
    headers: [
      'incoterm',
      'description',
      'paysInlandOrigin',
      'paysInternational',
      'paysInlandDest',
      'paysHandling',
      'paysInsurance',
      'paysDuties',
      'sapCode',
    ],
    rows: [
      ['EXW', 'Ex Works — comprador paga todo', 'true', 'true', 'true', 'true', 'true', 'true', ''],
      [
        'FOB',
        'Free On Board — comprador paga desde puerto',
        'false',
        'true',
        'true',
        'true',
        'true',
        'true',
        '',
      ],
      ['CIF', 'Cost Insurance and Freight', 'false', 'false', 'true', 'true', 'false', 'true', ''],
      [
        'DDP',
        'Delivered Duty Paid — vendedor paga todo',
        'false',
        'false',
        'false',
        'false',
        'false',
        'false',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importIncoterms(file),
  },
  'freight-providers': {
    entityName: 'Proveedores de Flete',
    headers: [
      'name',
      'transportModes',
      'countryCode',
      'taxId',
      'legalName',
      'fiscalAddress',
      'contactName',
      'email',
      'phone',
      'whatsapp',
      'website',
      'bankName',
      'accountNumber',
      'accountType',
      'beneficiaryName',
      'swiftCode',
      'serviceArea',
      'creditTermsDays',
      'rating',
      'notes',
      'sapCode',
    ],
    rows: [
      [
        'DHL',
        'AIR;LAND',
        'US',
        '12-3456789',
        'DHL International Inc.',
        '333 Twin Dolphin Dr, Redwood City CA',
        'Maria Lopez',
        'sales@dhl.com',
        '+1 800 225 5345',
        '',
        'https://dhl.com',
        'Wells Fargo',
        '1234567890',
        'CHECKING',
        'DHL International Inc.',
        'WFBIUS6S',
        'Global',
        '30',
        '5',
        'Cliente desde 2018',
        '',
      ],
      [
        'Maersk',
        'SEA',
        'DK',
        '12345678',
        'A.P. Moller-Maersk',
        'Esplanaden 50, Copenhagen',
        '',
        'sales@maersk.com',
        '',
        '',
        'https://maersk.com',
        '',
        '',
        '',
        '',
        '',
        'China-Bolivia, USA-Bolivia',
        '45',
        '4',
        '',
        '',
      ],
      [
        'ABC Logistics',
        'LAND;RAIL',
        'BO',
        '1023456789',
        'ABC Logistica SRL',
        'Av. Cristo Redentor #1234, Santa Cruz',
        'Juan Perez',
        'ventas@abclog.bo',
        '+591 3 333 4444',
        '+591 70012345',
        '',
        'Banco Mercantil',
        '987654321',
        'CHECKING',
        'ABC Logistica SRL',
        'BMSCBOLB',
        'Frontera CL/PE/AR',
        '15',
        '',
        '',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importFreightProviders(file),
  },
  'freight-services': {
    entityName: 'Servicios de Flete',
    headers: [
      'code',
      'name',
      'description',
      'category',
      'referencePrice',
      'priceCurrency',
      'pricingType',
      'sapCode',
    ],
    rows: [
      ['ADUANA', 'Trámite Aduanero', 'Despacho en frontera', 'ADUANA', '150', 'USD', 'FLAT', ''],
      ['LABOR', 'Mano de obra carga/descarga', '', 'LABOR', '0.50', 'USD', 'WEIGHT', ''],
      [
        'ALMACENAJE',
        'Depósito en aduana',
        'Costo por día y CBM',
        'ALMACENAJE',
        '5',
        'USD',
        'VOLUME',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importFreightServices(file),
  },
  'freight-locations': {
    entityName: 'Ubicaciones de Flete',
    headers: [
      'code',
      'name',
      'type',
      'address',
      'city',
      'state',
      'postalCode',
      'countryCode',
      'latitude',
      'longitude',
      'timeZone',
      'iataCode',
      'unLocode',
      'phone',
      'email',
      'website',
      'contactName',
      'notes',
      'sapCode',
    ],
    rows: [
      [
        'MIA',
        'Aeropuerto Internacional Miami',
        'AIRPORT',
        '2100 NW 42nd Ave',
        'Miami',
        'FL',
        '33126',
        'US',
        '25.7959',
        '-80.2870',
        'America/New_York',
        'MIA',
        '',
        '',
        '',
        'https://miami-airport.com',
        '',
        '',
        '',
      ],
      [
        'VVI',
        'Aeropuerto Viru Viru',
        'AIRPORT',
        'Carretera al Norte km 17',
        'Santa Cruz',
        'SC',
        '',
        'BO',
        '-17.6448',
        '-63.1354',
        'America/La_Paz',
        'VVI',
        '',
        '+591 3 338 5000',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'ARI',
        'Puerto de Arica',
        'SEAPORT',
        '',
        'Arica',
        'AP',
        '',
        'CL',
        '-18.4783',
        '-70.3126',
        'America/Santiago',
        '',
        'CLARI',
        '',
        '',
        'https://puertoarica.cl',
        '',
        'Puerto principal de salida BOL',
        '',
      ],
      [
        'ALM_SCZ',
        'Almacén Central Santa Cruz',
        'WAREHOUSE',
        'Av. Cristo Redentor km 7',
        'Santa Cruz',
        'SC',
        '',
        'BO',
        '-17.7833',
        '-63.1822',
        'America/La_Paz',
        '',
        '',
        '+591 3 444 4444',
        'almacen@empresa.com',
        '',
        'Juan Perez',
        'Almacén principal',
        '',
      ],
    ],
    importFn: (file) => masterDataService.importFreightLocations(file),
  },
  'freight-leg-catalog': {
    entityName: 'Tramos de Flete',
    headers: [
      'origin',
      'destination',
      'transportMode',
      'legType',
      'ratePerCbm',
      'ratePerKg',
      'ratePerContainer',
      'minimumCharge',
      'estimatedDays',
      'providerName',
      'sapCode',
    ],
    rows: [
      ['Shanghai', 'Arica', 'SEA', 'INTERNATIONAL', '50', '0.5', '2500', '300', '35', 'Maersk', ''],
      [
        'Arica',
        'La Paz',
        'LAND',
        'INLAND_DESTINATION',
        '0',
        '0.15',
        '',
        '150',
        '3',
        'ABC Logistics',
        '',
      ],
      ['Miami', 'La Paz', 'AIR', 'INTERNATIONAL', '0', '5.5', '', '500', '5', 'DHL', ''],
    ],
    importFn: (file) => masterDataService.importFreightLegs(file),
  },
};

const VALID_TABS: TabType[] = [
  'currencies',
  'exchange-rates',
  'lobs',
  'countries',
  'materials',
  'quote-types',
  'freight-providers',
  'incoterms',
  'freight-leg-catalog',
  'freight-services',
  'freight-locations',
  'profit-centers',
];

export function MasterDataPage() {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const activeTab = useMemo<TabType>(
    () => (VALID_TABS.includes(tabParam as TabType) ? (tabParam as TabType) : 'currencies'),
    [tabParam]
  );
  const setActiveTab = (tab: TabType) => navigate(`/master-data/${tab}`);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [addChildParent, setAddChildParent] = useState<LOB | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<{
    parent: { id: string; name: string; code: string; levelLabel: string };
    activeChildren: {
      id: string;
      name: string;
      code: string;
      level: number | string;
      levelLabel: string;
    }[];
  } | null>(null);
  const [isCascadeDeleting, setIsCascadeDeleting] = useState(false);
  // Incrementado al terminar un import — fuerza re-mount de tablas cuyo
  // fetch vive dentro del componente y no en el master-data-store.
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [editingItem, setEditingItem] = useState<Currency | LOB | Country | null>(null);

  // Material-specific state
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [tariffMaterial, setTariffMaterial] = useState<Material | null>(null);
  const [isTariffDialogOpen, setIsTariffDialogOpen] = useState(false);

  const {
    currencies,
    lobs,
    countries,
    isLoading,
    fetchCurrencies,
    createCurrency,
    updateCurrency,
    deleteCurrency,
    fetchLOBs,
    createLOB,
    updateLOB,
    deleteLOB,
    fetchCountries,
    createCountry,
    updateCountry,
    deleteCountry,
    quoteTypes,
    fetchQuoteTypes,
    createQuoteType,
    updateQuoteType,
    deleteQuoteType,
    syncExchangeRates,
    isSyncing,
  } = useMasterDataStore();

  const {
    materials,
    isLoading: isMaterialLoading,
    fetchMaterials,
    createMaterial,
    updateMaterial,
    deleteMaterial,
  } = useMaterialStore();

  const {
    configs,
    fetchConfigs: fetchIntegrationConfigs,
    triggerSync,
    syncing,
  } = useIntegrationStore();
  const sapConfig = configs.find((c) => c.type === 'SAP_S4HANA' && c.isActive);

  useEffect(() => {
    fetchCurrencies();
    fetchLOBs();
    fetchCountries();
    fetchMaterials();
    fetchQuoteTypes();
    fetchIntegrationConfigs();
  }, []);

  const handleSapSync = async (direction: 'inbound' | 'outbound') => {
    if (!sapConfig) return;
    try {
      const result = await triggerSync(sapConfig.id, direction);
      toast({
        title: 'Sincronización SAP completada',
        description: `${result.successCount} exitosos, ${result.failedCount} fallidos`,
        variant: 'success',
      });
      // Refresh materials after sync
      fetchMaterials();
    } catch (error: any) {
      toast({
        title: 'Error al sincronizar con SAP',
        description: error?.response?.data?.error || error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSyncRates = async () => {
    try {
      await syncExchangeRates();
      toast({
        title: 'Tasas actualizadas',
        description: 'Los tipos de cambio han sido sincronizados desde CriptoYa',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al sincronizar tipos de cambio',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = () => {
    if (activeTab === 'materials') {
      setEditingMaterial(null);
      setIsMaterialDialogOpen(true);
    } else {
      setEditingItem(null);
      setIsDialogOpen(true);
    }
  };

  const handleEdit = (item: Currency | LOB | Country | QuoteType) => {
    setEditingItem(item as Currency | LOB | Country);
    setIsDialogOpen(true);
  };

  const handleEditMaterial = (material: Material) => {
    setEditingMaterial(material);
    setIsMaterialDialogOpen(true);
  };

  const handleDelete = async (item: Currency | LOB | Country | QuoteType) => {
    if (
      !(await confirmar({
        title: '¿Estás seguro de que deseas eliminar este elemento?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    ) {
      return;
    }

    try {
      if (activeTab === 'currencies') {
        await deleteCurrency(item.id);
        toast({
          title: 'Divisa eliminada',
          description: 'La divisa ha sido eliminada correctamente',
          variant: 'success',
        });
      } else if (activeTab === 'lobs') {
        try {
          await deleteLOB(item.id);
          toast({
            title: 'LOB eliminado',
            description: 'El LOB ha sido eliminado correctamente',
            variant: 'success',
          });
        } catch (lobError: any) {
          const status = lobError?.response?.status;
          const details = lobError?.response?.data?.details;
          if (status === 409 && details?.reason === 'has_active_children') {
            setBlockedDelete({
              parent: details.parent,
              activeChildren: details.activeChildren ?? [],
            });
            return; // ya mostramos el dialog, no re-tirar el error
          }
          throw lobError;
        }
      } else if (activeTab === 'countries') {
        await deleteCountry(item.id);
        toast({
          title: 'País eliminado',
          description: 'El país ha sido eliminado correctamente',
          variant: 'success',
        });
      } else if (activeTab === 'quote-types') {
        await deleteQuoteType(item.id);
        toast({
          title: 'Tipo de cotización eliminado',
          description: 'El tipo de cotización ha sido eliminado correctamente',
          variant: 'success',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al eliminar',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteMaterial = async (material: Material) => {
    if (
      !(await confirmar({
        title: '¿Estás seguro de que deseas eliminar este material?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await deleteMaterial(material.id);
      toast({ title: 'Material eliminado', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleViewTariffHistory = (material: Material) => {
    setTariffMaterial(material);
    setIsTariffDialogOpen(true);
  };

  const handleSubmitCurrency = async (data: any) => {
    try {
      if (editingItem) {
        await updateCurrency(editingItem.id, data);
        toast({
          title: 'Divisa actualizada',
          description: 'La divisa ha sido actualizada correctamente',
          variant: 'success',
        });
      } else {
        await createCurrency(data);
        toast({
          title: 'Divisa creada',
          description: 'La divisa ha sido creada correctamente',
          variant: 'success',
        });
      }
      setIsDialogOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar la divisa',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitLOB = async (data: any) => {
    try {
      if (editingItem) {
        await updateLOB(editingItem.id, data);
        toast({
          title: 'LOB actualizado',
          description: 'El LOB ha sido actualizado correctamente',
          variant: 'success',
        });
      } else {
        await createLOB(data);
        toast({
          title: 'LOB creado',
          description: 'El LOB ha sido creado correctamente',
          variant: 'success',
        });
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      setAddChildParent(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar el LOB',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitCountry = async (data: any) => {
    try {
      if (editingItem) {
        await updateCountry(editingItem.id, data);
        toast({
          title: 'País actualizado',
          description: 'El país ha sido actualizado correctamente',
          variant: 'success',
        });
      } else {
        await createCountry(data);
        toast({
          title: 'País creado',
          description: 'El país ha sido creado correctamente',
          variant: 'success',
        });
      }
      setIsDialogOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar el país',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitQuoteType = async (data: any) => {
    try {
      if (editingItem) {
        await updateQuoteType(editingItem.id, data);
        toast({
          title: 'Tipo de cotización actualizado',
          description: 'El tipo de cotización ha sido actualizado correctamente',
          variant: 'success',
        });
      } else {
        await createQuoteType(data);
        toast({
          title: 'Tipo de cotización creado',
          description: 'El tipo de cotización ha sido creado correctamente',
          variant: 'success',
        });
      }
      setIsDialogOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar el tipo de cotización',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitMaterial = async (data: any) => {
    try {
      if (editingMaterial) {
        await updateMaterial(editingMaterial.id, data);
        toast({ title: 'Material actualizado', variant: 'success' });
      } else {
        await createMaterial(data);
        toast({ title: 'Material creado', variant: 'success' });
      }
      setIsMaterialDialogOpen(false);
      setEditingMaterial(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const tabs = [
    { id: 'currencies' as TabType, label: 'Divisas', count: currencies?.length ?? 0 },
    { id: 'exchange-rates' as TabType, label: 'Tipos de cambio' },
    { id: 'lobs' as TabType, label: 'Líneas de negocio', count: lobs?.length ?? 0 },
    { id: 'countries' as TabType, label: 'Países', count: countries?.length ?? 0 },
    { id: 'materials' as TabType, label: 'Materiales', count: materials?.length ?? 0 },
    { id: 'quote-types' as TabType, label: 'Tipos de cotización', count: quoteTypes?.length ?? 0 },
    { id: 'freight-providers' as TabType, label: 'Proveedores de flete' },
    { id: 'incoterms' as TabType, label: 'Incoterms' },
    { id: 'freight-leg-catalog' as TabType, label: 'Tramos y tarifas' },
    { id: 'freight-services' as TabType, label: 'Servicios de flete' },
    { id: 'freight-locations' as TabType, label: 'Ubicaciones' },
    { id: 'profit-centers' as TabType, label: 'Centros de beneficio' },
  ];

  return (
    <AppLayout>
      <div className="container mx-auto p-8">
        <div className="space-y-6">
          {/* Header */}
          <div data-tour="master-data-header" className="flex items-center justify-between">
            <div>
              <div className="mb-3">
                <EtiquetaDePagina tono="neutro">Datos Maestros</EtiquetaDePagina>
              </div>
              <h1 className={CLASE_TITULO_DE_PAGINA}>Datos Maestros</h1>
              <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
                Configuración de divisas, líneas de negocio, países, materiales y tipos de
                cotización
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'currencies' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-tour="master-data-sync-btn"
                      variant="outline"
                      onClick={handleSyncRates}
                      disabled={isSyncing}
                    >
                      {isSyncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sincronizar TC
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Consulta los tipos de cambio actuales de USDT y USDC contra BOB desde exchanges
                    P2P (Binance, Bybit, Bitget) y actualiza las tasas en el sistema
                  </TooltipContent>
                </Tooltip>
              )}
              {sapConfig && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={syncing === sapConfig.id}>
                      <RefreshCw
                        className={`h-4 w-4 mr-2 ${syncing === sapConfig.id ? 'animate-spin' : ''}`}
                      />
                      Sincronizar SAP
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleSapSync('inbound')}>
                      <ArrowDownToLine className="h-4 w-4 mr-2" />
                      Importar desde SAP
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSapSync('outbound')}>
                      <ArrowUpFromLine className="h-4 w-4 mr-2" />
                      Exportar a SAP
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {(user?.role === 'COMPANY_ADMIN' || user?.role === 'PLATFORM_ADMIN') &&
                IMPORT_CONFIGS[activeTab] && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" onClick={() => setShowImport(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Importar CSV
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Importar datos masivamente desde un archivo CSV
                    </TooltipContent>
                  </Tooltip>
                )}
              {activeTab !== 'freight-providers' &&
                activeTab !== 'incoterms' &&
                activeTab !== 'exchange-rates' &&
                activeTab !== 'freight-leg-catalog' &&
                activeTab !== 'freight-services' &&
                activeTab !== 'freight-locations' &&
                activeTab !== 'profit-centers' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button data-tour="master-data-create-btn" onClick={handleCreate}>
                        <Plus className="w-4 h-4 mr-2" />
                        {activeTab === 'materials'
                          ? 'Nuevo Material'
                          : activeTab === 'quote-types'
                            ? 'Nuevo Tipo'
                            : 'Crear Nuevo'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {activeTab === 'currencies' && 'Agregar una nueva divisa al sistema'}
                      {activeTab === 'lobs' && 'Crear una nueva línea de negocio'}
                      {activeTab === 'countries' && 'Agregar un nuevo país'}
                      {activeTab === 'materials' &&
                        'Registrar un nuevo material con su partida arancelaria'}
                      {activeTab === 'quote-types' && 'Crear un nuevo tipo de cotización'}
                    </TooltipContent>
                  </Tooltip>
                )}
            </div>
          </div>

          {/* Tabs */}
          {/*
            Las pestañas en una fila que se desliza, como las del artefacto: con doce,
            en columnas angostas se partían en tres renglones («Tipos / de / Cambio»).
          */}
          <div
            data-tour="master-data-tabs"
            className="flex gap-x-6 overflow-x-auto overflow-y-hidden border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                aria-pressed={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`-mb-px inline-flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-b-[3px] px-0.5 text-[15px] transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary font-semibold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <Card data-tour="master-data-content">
            <CardHeader>
              <CardTitle>
                {activeTab === 'currencies' && 'Divisas'}
                {activeTab === 'exchange-rates' && 'Tipos de Cambio (por fecha)'}
                {activeTab === 'lobs' && 'Líneas de negocio (LOB)'}
                {activeTab === 'countries' && 'Países'}
                {activeTab === 'materials' && 'Materiales'}
                {activeTab === 'quote-types' && 'Tipos de Cotización'}
                {activeTab === 'freight-providers' && 'Proveedores de Transporte'}
                {activeTab === 'incoterms' && 'Configuración de Incoterms'}
                {activeTab === 'freight-leg-catalog' && 'Catálogo de Tramos'}
                {activeTab === 'freight-services' && 'Servicios de Flete'}
                {activeTab === 'freight-locations' && 'Localizaciones'}
                {activeTab === 'profit-centers' && 'Profit Centers (SAP)'}
              </CardTitle>
              <CardDescription>
                {activeTab === 'currencies' &&
                  'Administra las monedas disponibles en el sistema. Las divisas USDT y USDC se sincronizan automáticamente cada 30 minutos con tasas P2P reales. Usa "Sincronizar TC" para forzar una actualización manual.'}
                {activeTab === 'exchange-rates' &&
                  'Registrá el tipo de cambio por par de monedas y fecha efectiva. Al jalar costos desde una cotización upstream con moneda distinta, el sistema busca el rate vigente más reciente. Si falta el rate para un par, la carga se bloquea hasta que lo registres.'}
                {activeTab === 'lobs' &&
                  'Organiza tus líneas de negocio en jerarquía de 3 niveles (Sector → Vertical → Marca). Cada cotización se asocia a un LOB para facilitar reportes y análisis.'}
                {activeTab === 'countries' &&
                  'Países disponibles para asignar a las cotizaciones. Se usan para segmentar por mercado y aplicar reglas de pricing especificas por región.'}
                {activeTab === 'materials' &&
                  'Catálogo de materiales con partida arancelaria y gravamen histórico. Al crear una cotización, seleccionas materiales de este catálogo como items de línea.'}
                {activeTab === 'quote-types' &&
                  'Define los tipos de cotización disponibles en el sistema. Cada tipo puede encadenarse a otro tipo upstream para heredar precios de referencia.'}
                {activeTab === 'freight-providers' &&
                  'Administra los proveedores de transporte disponibles. Cada proveedor tiene modos de transporte asociados y datos de contacto.'}
                {activeTab === 'incoterms' &&
                  'Define qué componentes de costo paga el comprador (importador) según el incoterm. Esto determina qué tramos de flete se incluyen al cotizar una importación.'}
                {activeTab === 'freight-leg-catalog' &&
                  'Tramos reutilizables de transportistas, independientes de cualquier ruta. Úsalos al armar rutas custom en cotizaciones de importación.'}
                {activeTab === 'freight-services' &&
                  'Catálogo de servicios adicionales de flete (manipuleo, almacenaje, despacho aduanero, etc.) con precio referencial. El precio se aplica como referencia al cotizar — no se cobra por proveedor sino por servicio.'}
                {activeTab === 'freight-locations' &&
                  'Catálogo de aeropuertos, puertos, terminales, almacenes y aduanas. Se usan como origen/destino al armar tramos.'}
                {activeTab === 'profit-centers' &&
                  'Centros de beneficio sincronizados desde SAP (API_PROFITCENTER_SRV). Read-only — para modificar el catálogo, editá en SAP y sincronizá.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeTab === 'currencies' && (
                <CurrencyTable
                  currencies={currencies}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={isLoading}
                />
              )}
              {activeTab === 'exchange-rates' && (
                <ExchangeRateManager key={tableRefreshKey} currencies={currencies} />
              )}
              {activeTab === 'lobs' && (
                <LOBTable
                  lobs={lobs}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCreateInline={async (data) => {
                    try {
                      await createLOB(data as any);
                      toast({
                        title: 'LOB creado',
                        description: `${data.name} agregado`,
                        variant: 'success',
                      });
                    } catch (error: any) {
                      toast({
                        title: 'Error',
                        description:
                          error?.response?.data?.error || error?.message || 'Error al crear LOB',
                        variant: 'destructive',
                      });
                      throw error;
                    }
                  }}
                  onAddChild={(parent) => {
                    setEditingItem(null);
                    setAddChildParent(parent);
                    setIsDialogOpen(true);
                  }}
                  isLoading={isLoading}
                />
              )}
              {activeTab === 'countries' && (
                <CountryTable
                  countries={countries}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={isLoading}
                />
              )}
              {activeTab === 'materials' && (
                <MaterialTable
                  materials={materials}
                  onEdit={handleEditMaterial}
                  onDelete={handleDeleteMaterial}
                  onViewTariffHistory={handleViewTariffHistory}
                  isLoading={isMaterialLoading}
                />
              )}
              {activeTab === 'quote-types' && (
                <QuoteTypeTable
                  quoteTypes={quoteTypes}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={isLoading}
                />
              )}
              {activeTab === 'freight-providers' && <FreightProviderTable key={tableRefreshKey} />}
              {activeTab === 'incoterms' && <IncotermConfigTable key={tableRefreshKey} />}
              {activeTab === 'freight-leg-catalog' && (
                <FreightLegCatalogTable key={tableRefreshKey} />
              )}
              {activeTab === 'freight-services' && <FreightServiceTable key={tableRefreshKey} />}
              {activeTab === 'freight-locations' && <FreightLocationTable key={tableRefreshKey} />}
              {activeTab === 'profit-centers' && <ProfitCenterTable key={tableRefreshKey} />}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog for Create/Edit (Currencies, LOBs, Countries) */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Editar' : 'Crear'} {activeTab === 'currencies' && 'Divisa'}
              {activeTab === 'lobs' && 'LOB'}
              {activeTab === 'countries' && 'País'}
              {activeTab === 'quote-types' && 'Tipo de Cotización'}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? 'Actualiza los datos del elemento'
                : 'Completa los datos para crear el nuevo elemento'}
            </DialogDescription>
          </DialogHeader>

          {activeTab === 'currencies' && (
            <CurrencyForm
              currency={editingItem as Currency}
              onSubmit={handleSubmitCurrency}
              onCancel={() => setIsDialogOpen(false)}
              isLoading={isLoading}
            />
          )}
          {activeTab === 'lobs' && (
            <LOBForm
              lob={editingItem as LOB}
              lobs={lobs}
              presetParent={addChildParent ?? undefined}
              onSubmit={handleSubmitLOB}
              onCancel={() => {
                setIsDialogOpen(false);
                setAddChildParent(null);
              }}
              isLoading={isLoading}
            />
          )}
          {activeTab === 'countries' && (
            <CountryForm
              country={editingItem as Country}
              onSubmit={handleSubmitCountry}
              onCancel={() => setIsDialogOpen(false)}
              isLoading={isLoading}
            />
          )}
          {activeTab === 'quote-types' && (
            <QuoteTypeForm
              quoteType={editingItem as unknown as QuoteType}
              quoteTypes={quoteTypes}
              onSubmit={handleSubmitQuoteType}
              onCancel={() => setIsDialogOpen(false)}
              isLoading={isLoading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Material Create/Edit Dialog */}
      <Dialog open={isMaterialDialogOpen} onOpenChange={setIsMaterialDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? 'Editar Material' : 'Nuevo Material'}</DialogTitle>
            <DialogDescription>
              {editingMaterial
                ? 'Actualiza los datos del material'
                : 'Completa los datos para crear un nuevo material'}
            </DialogDescription>
          </DialogHeader>
          <MaterialForm
            material={editingMaterial || undefined}
            onSubmit={handleSubmitMaterial}
            onCancel={() => setIsMaterialDialogOpen(false)}
            isLoading={isMaterialLoading}
          />
        </DialogContent>
      </Dialog>

      {/* Blocked Delete Dialog (LOB with active children) */}
      <Dialog open={!!blockedDelete} onOpenChange={(o) => !o && setBlockedDelete(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              No se puede eliminar este LOB
            </DialogTitle>
            <DialogDescription>
              <strong>{blockedDelete?.parent.name}</strong> ({blockedDelete?.parent.levelLabel})
              tiene{' '}
              <strong>
                {blockedDelete?.activeChildren.length}{' '}
                {blockedDelete?.activeChildren.length === 1 ? 'hijo activo' : 'hijos activos'}
              </strong>
              . Eliminá o desactivá primero los siguientes elementos:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
            {blockedDelete?.activeChildren.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{c.code}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{c.levelLabel}</span>
              </div>
            ))}
          </div>
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p className="font-semibold text-destructive mb-1">
              ¿Querés eliminarlo todo de una sola vez?
            </p>
            <p className="text-xs text-muted-foreground">
              Esto soft-deletea <strong>{blockedDelete?.parent.name}</strong> y
              <strong> todos sus descendientes</strong> (incluyendo nietos). La acción es reversible
              reactivando los registros manualmente.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setBlockedDelete(null)}
              disabled={isCascadeDeleting}
            >
              Cerrar
            </Button>
            <Button
              variant="destructive"
              disabled={isCascadeDeleting || !blockedDelete}
              onClick={async () => {
                if (!blockedDelete) return;
                if (
                  !(await confirmar({
                    title: `¿Eliminar «${blockedDelete.parent.name}» y todo lo que cuelga de él?`,
                    description: 'Se eliminan también todos sus descendientes.',
                    variant: 'destructive',
                    confirmText: 'Sí, eliminar todo',
                  }))
                )
                  return;
                try {
                  setIsCascadeDeleting(true);
                  await deleteLOB(blockedDelete.parent.id, { cascade: true });
                  toast({
                    title: 'Eliminación en cascada completada',
                    description: `Se eliminó "${blockedDelete.parent.name}" y todos sus descendientes`,
                    variant: 'success',
                  });
                  setBlockedDelete(null);
                } catch (err: any) {
                  toast({
                    title: 'Error',
                    description: err?.response?.data?.error || 'No se pudo eliminar en cascada',
                    variant: 'destructive',
                  });
                } finally {
                  setIsCascadeDeleting(false);
                }
              }}
            >
              {isCascadeDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando…
                </>
              ) : (
                <>Eliminar todo en cascada</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tariff History Dialog */}
      <TariffHistoryDialog
        material={tariffMaterial}
        open={isTariffDialogOpen}
        onOpenChange={setIsTariffDialogOpen}
      />

      {/* Import CSV Dialog */}
      {IMPORT_CONFIGS[activeTab] && (
        <MasterDataImportDialog
          open={showImport}
          onOpenChange={setShowImport}
          entityName={IMPORT_CONFIGS[activeTab]!.entityName}
          templateHeaders={IMPORT_CONFIGS[activeTab]!.headers}
          templateRows={IMPORT_CONFIGS[activeTab]!.rows}
          importFn={IMPORT_CONFIGS[activeTab]!.importFn}
          onSuccess={() => {
            if (activeTab === 'currencies') fetchCurrencies();
            else if (activeTab === 'lobs') fetchLOBs();
            else if (activeTab === 'countries') fetchCountries();
            else if (activeTab === 'materials') fetchMaterials();
            else if (activeTab === 'quote-types') fetchQuoteTypes();
            // Para entidades cuyo fetch vive dentro del componente
            // (FreightProviderTable, IncotermConfigTable, etc.) cambiamos
            // tableRefreshKey para forzar re-mount y re-fetch.
            setTableRefreshKey((k) => k + 1);
            toast({
              title: 'Importación completada',
              description: `Los datos de ${IMPORT_CONFIGS[activeTab]!.entityName} han sido importados correctamente`,
              variant: 'success',
            });
          }}
        />
      )}
    </AppLayout>
  );
}

export default MasterDataPage;
